import React, { useState, useEffect } from 'react';
import { Sun, Cloud, CloudRain, Loader2, MapPin } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Compact Weather Icon
const WeatherIcon = ({ icon, className = "h-5 w-5" }) => {
  switch (icon) {
    case 'sun':
      return <Sun className={`${className} text-amber-500`} />;
    case 'cloud':
      return <Cloud className={`${className} text-gray-400`} />;
    case 'rain':
      return <CloudRain className={`${className} text-blue-400`} />;
    case 'sun-cloud':
    default:
      return (
        <div className="relative inline-flex items-center justify-center">
          <Sun className="h-4 w-4 text-amber-500" />
          <Cloud className="h-3 w-3 text-gray-400 absolute -bottom-0.5 -right-1" />
        </div>
      );
  }
};

const YourBriefingSection = () => {
  const { t, isKinyarwanda } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weather, setWeather] = useState({
    temp: 22,
    condition: 'Partly cloudy',
    icon: 'sun-cloud',
    humidity: null,
    wind_speed: null,
  });
  const [loading, setLoading] = useState(true);

  // Fetch weather on mount
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/weather?city=Kigali&country=RW`);
        setWeather({
          temp: response.data.temp,
          condition: response.data.condition,
          icon: response.data.icon,
          humidity: response.data.humidity,
          wind_speed: response.data.wind_speed,
        });
      } catch (error) {
        console.error('Failed to fetch weather:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchWeather();
    const weatherTimer = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(weatherTimer);
  }, []);

  // Update date every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Format date based on language
  const locale = isKinyarwanda ? 'rw-RW' : 'en-US';
  const dayName = currentDate.toLocaleDateString(locale, { weekday: 'long' });
  const monthDay = currentDate.toLocaleDateString(locale, { month: 'long', day: 'numeric' });

  return (
    <div className="mb-4 sm:mb-6 pt-1">
      {/* Sleek, Compact Header Container */}
      <div className="flex items-center justify-between gap-4 pb-2 border-b border-gray-100 dark:border-gray-800/80">
        {/* Left: Your briefing title and date */}
        <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-white leading-tight">
            {t('sections.yourBriefing') || 'Your briefing'}
          </h1>
          <span className="inline-flex items-center text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
            <span className="hidden sm:inline text-gray-300 dark:text-gray-700 mr-2">•</span>
            {dayName}, {monthDay}
          </span>
        </div>
        
        {/* Right: Sleek Weather Pill Badge */}
        <div 
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border border-gray-200/80 dark:border-gray-800 shadow-sm hover:shadow-md transition-all flex-shrink-0 group cursor-default"
          title={`${weather.condition} in Kigali`}
        >
          <div className="flex items-center justify-center">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            ) : (
              <WeatherIcon icon={weather.icon} className="h-4 w-4" />
            )}
          </div>
          <span className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white tracking-tight">
            {weather.temp}°C
          </span>
          <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1 border-l border-gray-200 dark:border-gray-700/80 pl-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            Kigali
          </span>
        </div>
      </div>
    </div>
  );
};

export default YourBriefingSection;
